import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import TrianglesReader from 'Loader/serialization/common/TrianglesReader.js';
import type Triangle from '@data/geometry/Triangle';
import type FVector from '@data/geometry/FVector';
import GeomSerializable, { GeomHeaderSchema } from './GeomSerializable';
import { GeometryChunkId } from './GeometryChunkId';

type PreviousSchema = SchemaV5 | LatestGroundSchema;

type BaseSchema = {
    version: number;
};
type SchemaV5 = BaseSchema & {
    res: FVector;
    points: FVector[];
    // 0 = internal, 1 = mid, 2 = external
    numberOfTrisPerZone: number[];
    triangles: Triangle[];
    triIDsInt: number[];
    triIDsMid: number[];
    triIDsExt: number[];
};
export type LatestGroundSchema = SchemaV5;

class GroundSerializer extends GeomSerializable<PreviousSchema, LatestGroundSchema> {
    protected readonly expectedChunkId = GeometryChunkId.Ground;
    private static readonly LATEST_VERSION = 5;

    protected migrateToLatest(anyPrevious: PreviousSchema): LatestGroundSchema {
        let migrated: PreviousSchema = anyPrevious;
        while (migrated.version < GroundSerializer.LATEST_VERSION) {
            const migrate = this.getMigrationForVersion(migrated.version);
            if (!migrate) {
                throw new Error(`Missing Ground migration for version ${migrated.version}`);
            }
            migrated = migrate(migrated);
        }
        return migrated as LatestGroundSchema;
    }

    protected deserializeContent(header: GeomHeaderSchema, reader: BinaryReader): LatestGroundSchema {
        let parsed: PreviousSchema;
        if (header.version >= 5) {
            parsed = this.deserializeV5(header, reader);
        } else {
            throw new Error(`Unsupported Ground version: ${header.version}. Update your files`);
        }

        return this.migrateToLatest(parsed);
    }

    private getMigrationForVersion(_version: number): ((schema: PreviousSchema) => PreviousSchema) | undefined {
        return undefined;
    }

    private deserializeV5(header: GeomHeaderSchema, reader: BinaryReader): SchemaV5 {
        const points: FVector[] = [];
        const numberOfTrisPerZone: number[] = [];
        const triangles: Triangle[] = [];
        const triIDsInt: number[] = [];
        const triIDsMid: number[] = [];
        const triIDsExt: number[] = [];

        const res = reader.readVec3();

        const zones = ['internal', 'mid', 'external'];
        let zoneIndex = 0;
        for (const _zone of zones) {
            const numberOfTrisForZone = TrianglesReader.read(reader, points, triangles);
            for (let i = 0; i < numberOfTrisForZone; i++) {
                const id = reader.readUInt32();
                if (zoneIndex === 0) triIDsInt.push(id);
                if (zoneIndex === 1) triIDsMid.push(id);
                if (zoneIndex === 2) triIDsExt.push(id);
            }
            numberOfTrisPerZone.push(numberOfTrisForZone);
            zoneIndex += 1;
        }
        return {
            version: header.version,
            res,
            points,
            numberOfTrisPerZone,
            triangles,
            triIDsInt,
            triIDsMid,
            triIDsExt,
        };
    }
}

export default GroundSerializer;
